import { hasYesOrNo } from 'models/validate';
import offenseAddress from 'forms/shared/validators/models/location/models/offense';
import chargeValidator from 'forms/shared/validators/models/charge/charge';
import legalSentenceValidator from 'forms/sections/legal/shared/sentence/sentence';
import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { LocationModel } from 'forms/shared/types';
import { DeepSafePartial } from 'types/types';
import { isSafeUnitedStates } from 'helpers/locations';
import { DateTime } from 'luxon';
import { LegalPoliceOtherOffenseItemModel } from '../types';

export const legalPoliceOtherOffenseItemValidator: ValidateJSModel<LegalPoliceOtherOffenseItemModel> = {
  // For Date, validation is the same for both PVQ and non-PVQ
  Date: (value, attributes, attributeName, options) => ({
    date: {
      earliest: getSafeDateTime(setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST) as DateTime).datetime,
      latest: DEFAULT_LATEST,
    },
    presence: true,
  }),
  // Validation is the same for both PVQ and non-PVQ
  Description: { presence: true, hasValue: true },
  // Validation is the same for both PVQ and non-PVQ
  InvolvedViolence: {
    presence: true,
    hasValue: { validator: hasYesOrNo },
  },
  InvolvedFirearms: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    if (options?.requireLegalPoliceFirearms) {
      return {
        hasValue: { validator: hasYesOrNo },
        presence: true,
      };
    }

    return {};
  },
  InvolvedExplosives: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  InvolvedSubstances: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    if (options?.requireLegalPoliceDrugs) {
      return {
        hasValue: { validator: hasYesOrNo },
        presence: true,
      };
    }

    return {};
  },
  InvolvedAlcohol: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  OffenseDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        /* Added to display error: The date should be after your date of birth. */
        date: {
          requireDay: false,
          earliest: getSafeDateTime(setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST) as DateTime).datetime,
          latest: DEFAULT_LATEST,
          tooEarly: 'error.date.min',
        },
      };
    }

    return {};
  },
  OffenseAddress: (value: DeepSafePartial<LocationModel | undefined>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          validator: {
            ...pvqLocationModelValidator,
            county: { presence: isSafeUnitedStates(value) },
          } as ValidateJSModel<LocationModel>,
        },
        presence: true,
      };
    }

    return {};
  },
  ReasonNotCharged: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: true };
    }

    return {};
  },
  // Validation is the same for both PVQ and non-PVQ
  CourtName: { presence: true, hasValue: true },
  CourtAddress: (value: DeepSafePartial<LocationModel>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          requireStreet: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }

    return {
      presence: true,
      location: { validator: offenseAddress },
    };
  },
  Charges: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return {
      presence: true,
      accordion: { validator: chargeValidator, ignoreBranch: true }
    };
  },
  WasSentenced: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  AwaitingTrial: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'No') {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  AwaitingTrialExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'No') {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  Sentence: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (attributes?.WasSentenced?.value === 'Yes') {
      return {
        presence: true,
        model: {
          validator: legalSentenceValidator,
          offenseDate: attributes.Date
        }
      };
    }

    return {};
  },
};
